import { KVMeasurement, KVPlaneIntersection, KVPosition } from 'types'

export enum STEP_FILE_TYPES {
  MEASUREMENTS = 'measurements',
  PLANE_INTERSECTION = 'plane_intersection',
}

export function convertMeasurementsToStepFile(
  measurements: KVMeasurement[] | undefined,
  decimalPlaces: number = 3
): string {
  if (!measurements || measurements.length === 0) {
    return generateEmptyStepFile()
  }

  const stepHeader = generateStepHeader(STEP_FILE_TYPES.MEASUREMENTS)
  const stepData = generateStepData(measurements, decimalPlaces)
  const stepFooter = generateStepFooter()

  return stepHeader + stepData + stepFooter
}

export function convertPlaneIntersectionToStepFile(
  planeIntersection: KVPlaneIntersection | undefined,
  decimalPlaces: number = 3
): string {
  if (
    !planeIntersection ||
    planeIntersection.planeIntersectionPoints.length === 0
  ) {
    return generateEmptyStepFile()
  }

  const stepHeader = generateStepHeader(STEP_FILE_TYPES.PLANE_INTERSECTION)
  const stepData = generatePlaneIntersectionStepData(
    planeIntersection,
    decimalPlaces
  )
  const stepFooter = generateStepFooter()

  return stepHeader + stepData + stepFooter
}

function parsePosition(positionString: string): KVPosition | null {
  try {
    // Position strings are in format "X=-2.512 Y=213.841 Z=252.967"
    const xMatch = positionString.match(/X=([-\d.]+)/)
    const yMatch = positionString.match(/Y=([-\d.]+)/)
    const zMatch = positionString.match(/Z=([-\d.]+)/)

    if (xMatch && yMatch && zMatch) {
      return new KVPosition(
        parseFloat(xMatch[1]),
        parseFloat(yMatch[1]),
        parseFloat(zMatch[1])
      )
    }
  } catch (error) {
    console.warn('Failed to parse position string:', positionString, error)
  }
  return null
}

/**
 * Convert coordinates from Unreal Engine coordinate system to CAD coordinate system
 * Unreal: Left-handed, Z-up
 * CAD: Right-handed, Z-up
 */
function convertCoordinates(point: KVPosition): KVPosition {
  // Convert from left-handed to right-handed coordinate system
  // by negating the Y-axis
  return new KVPosition(point.x, -point.y, point.z)
}

function generateStepHeader(stepFileType: STEP_FILE_TYPES): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

  return `ISO-10303-21;
HEADER;
FILE_DESCRIPTION(('Data exported from Kestrel'), '2;1');
FILE_NAME('${stepFileType}_${timestamp}.step', '${new Date().toISOString()}', ('Kestrel'), ('Gecko Robotics'), 'Kestrel Export', 'Kestrel', '');
FILE_SCHEMA(('AUTOMOTIVE_DESIGN { 1 2 10303 214 0 1 1 1 }'));
ENDSEC;

DATA;
`
}

function generateStepData(
  measurements: KVMeasurement[],
  decimalPlaces: number = 3
): string {
  let stepData = ''
  let entityId = 1

  // Minimal STEP structure for maximum compatibility
  const representationContextId = entityId++
  stepData += `#${representationContextId} = GEOMETRIC_REPRESENTATION_CONTEXT(3);\n`

  const geometricItems: number[] = []

  measurements.forEach((measurement, measurementIndex) => {
    stepData += `/* Measurement: ${measurement.label || `Measurement ${measurementIndex + 1}`} */\n`

    if (measurement.segments && measurement.segments.length > 0) {
      measurement.segments.forEach((segment, segmentIndex) => {
        const startPoint = parsePosition(
          segment.measurementSegmentStartPosition
        )
        const endPoint = parsePosition(segment.measurementSegmentEndPosition)

        if (startPoint && endPoint) {
          // Apply coordinate system conversion
          const convertedStartPoint = convertCoordinates(startPoint)
          const convertedEndPoint = convertCoordinates(endPoint)

          const startPointId = entityId++
          const endPointId = entityId++
          const lineId = entityId++

          // Create label for this segment
          const measurementLabel =
            measurement.label || `Measurement ${measurementIndex + 1}`
          const segmentLabel = `${measurementLabel} - Segment ${segmentIndex + 1}`

          // Point definitions with labels
          stepData += `#${startPointId} = CARTESIAN_POINT('${segmentLabel} Start', (${convertedStartPoint.x.toFixed(decimalPlaces)}, ${convertedStartPoint.y.toFixed(decimalPlaces)}, ${convertedStartPoint.z.toFixed(decimalPlaces)}));\n`
          stepData += `#${endPointId} = CARTESIAN_POINT('${segmentLabel} End', (${convertedEndPoint.x.toFixed(decimalPlaces)}, ${convertedEndPoint.y.toFixed(decimalPlaces)}, ${convertedEndPoint.z.toFixed(decimalPlaces)}));\n`

          // Polyline with label
          stepData += `#${lineId} = POLYLINE('${segmentLabel}', (#${startPointId}, #${endPointId}));\n`

          geometricItems.push(lineId)
        }
      })
    }
  })

  // Minimal representation
  if (geometricItems.length > 0) {
    const shapeRepresentationId = entityId++
    const itemsList = geometricItems.map((id) => `#${id}`).join(', ')
    stepData += `#${shapeRepresentationId} = GEOMETRIC_REPRESENTATION('', (${itemsList}), #${representationContextId});\n`
  }

  return stepData
}

function generatePlaneIntersectionStepData(
  planeIntersection: KVPlaneIntersection,
  decimalPlaces: number = 3
): string {
  let stepData = ''
  let entityId = 1

  // Minimal STEP structure: define a context
  const representationContextId = entityId++
  stepData += `#${representationContextId} = GEOMETRIC_REPRESENTATION_CONTEXT(3);\n`

  const vertexIds: number[] = []

  stepData += `/* Plane Intersection: ${planeIntersection.planeIntersectionLabel} */\n`

  // For each point, create a Cartesian point and then wrap it in a vertex.
  planeIntersection.planeIntersectionPoints.forEach((point, pointIndex) => {
    const kvPoint = new KVPosition(point.x, point.y, point.z)
    const convertedPoint = convertCoordinates(kvPoint)
    const cartesianPointId = entityId++
    const pointLabel = `${planeIntersection.planeIntersectionLabel} - Point ${pointIndex + 1}`

    stepData += `#${cartesianPointId} = CARTESIAN_POINT('${pointLabel} Coordinate', (${convertedPoint.x.toFixed(decimalPlaces)}, ${convertedPoint.y.toFixed(decimalPlaces)}, ${convertedPoint.z.toFixed(decimalPlaces)}));\n`

    vertexIds.push(cartesianPointId)
  })

  // Instead of using GEOMETRIC_REPRESENTATION directly, build a GEOMETRIC_SET of the vertices...
  if (vertexIds.length > 0) {
    const geometricSetId = entityId++
    const itemsList = vertexIds.map((id) => `#${id}`).join(', ')
    stepData += `#${geometricSetId} = GEOMETRIC_SET('', (${itemsList}));\n`

    // ...and wrap that set inside a SHAPE_REPRESENTATION, which many CAD tools recognize.
    const shapeRepresentationId = entityId++
    stepData += `#${shapeRepresentationId} = SHAPE_REPRESENTATION('', (#${geometricSetId}), #${representationContextId});\n`
  }
  return stepData
}

function generateStepFooter(): string {
  return `ENDSEC;
END-ISO-10303-21;
`
}

function generateEmptyStepFile(): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

  return `ISO-10303-21;
HEADER;
FILE_DESCRIPTION(('Empty measurement file - no measurements available'), '2;1');
FILE_NAME('empty_measurements_${timestamp}.step', '${new Date().toISOString()}', ('Kestrel'), ('Gecko Robotics'), 'Kestrel Export', 'Kestrel', '');
FILE_SCHEMA(('AUTOMOTIVE_DESIGN { 1 2 10303 214 0 1 1 1 }'));
ENDSEC;

DATA;
/* No measurements available */
ENDSEC;
END-ISO-10303-21;
`
}
